import { useState, useEffect } from 'react';
import { CheckCircle2, XCircle, Edit, Loader2, AlertCircle, Briefcase } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';

interface ConsultantSpaceReviewProps {
  companyId: string;
  accessToken: string;
  consultantId: string;
  onComplete: () => void;
}

interface SpacePlanningSession {
  id: string;
  companyId: string;
  createdBy: string;
  createdAt: string;
  status: string;
  answers: any;
  aiRecommendation: {
    architecture: string;
    confidence: number;
    reasoning: string;
    suggestedSpaces: Array<{
      name: string;
      type: string;
      teams: string[];
      estimatedTicketVolume?: string;
    }>;
    warnings?: string[];
  };
}

import { SpaceAssignment } from './SpaceAssignment';

export function ConsultantSpaceReview({
  companyId,
  accessToken,
  consultantId,
  onComplete,
}: ConsultantSpaceReviewProps) {
  const [session, setSession] = useState<SpacePlanningSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [decision, setDecision] = useState<'approve' | 'modify' | 'reject' | null>(null);
  const [notes, setNotes] = useState('');
  const [modifiedSpaces, setModifiedSpaces] = useState<any[]>([]);
  const [showAssignment, setShowAssignment] = useState(false);

  useEffect(() => {
    loadSession();
  }, [companyId]);

  const loadSession = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/space-planning/${companyId}/session`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSession(data.session);
        setModifiedSpaces(data.session.aiRecommendation.suggestedSpaces);
      } else {
        toast.error('Failed to load space planning session');
      }
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Failed to load space planning session');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/space-planning/${companyId}/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            decision: decision === 'modify' ? 'modified' : 'approved',
            modifiedRecommendation:
              decision === 'modify'
                ? {
                    ...session!.aiRecommendation,
                    suggestedSpaces: modifiedSpaces,
                  }
                : null,
            notes,
            consultantId,
          }),
        }
      );

      if (response.ok) {
        toast.success('Space plan approved and spaces created');
        setShowAssignment(true);
      } else {
        toast.error('Failed to approve space plan');
      }
    } catch (error) {
      console.error('Error approving space plan:', error);
      toast.error('Failed to approve space plan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/space-planning/${companyId}/reject`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            notes,
            consultantId,
          }),
        }
      );

      if (response.ok) {
        toast.success('Space plan sent back to client for revision');
        onComplete();
      } else {
        toast.error('Failed to reject space plan');
      }
    } catch (error) {
      console.error('Error rejecting space plan:', error);
      toast.error('Failed to reject space plan');
    } finally {
      setSubmitting(false);
    }
  };

  const addSpace = () => {
    setModifiedSpaces([
      ...modifiedSpaces,
      {
        name: 'New Space',
        type: 'jsm',
        teams: [],
        estimatedTicketVolume: '0%',
      },
    ]);
  };

  const removeSpace = (index: number) => {
    setModifiedSpaces(modifiedSpaces.filter((_, i) => i !== index));
  };

  const updateSpace = (index: number, field: string, value: any) => {
    const updated = [...modifiedSpaces];
    updated[index] = { ...updated[index], [field]: value };
    setModifiedSpaces(updated);
  };

  if (showAssignment) {
    return (
      <SpaceAssignment 
        companyId={companyId} 
        accessToken={accessToken} 
        onComplete={onComplete} 
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!session) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <AlertCircle className="size-12 mx-auto mb-3 text-gray-400" />
          <p className="font-medium">No space planning session found</p>
        </CardContent>
      </Card>
    );
  }

  const getAnswerLabel = (key: string, value: string) => {
    // Format answer values for display
    return value.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getArchitectureLabel = (arch: string) => {
    switch (arch) {
      case 'single-space':
        return 'Single Space';
      case 'multiple-spaces':
        return 'Multiple Spaces';
      case 'hybrid':
        return 'Hybrid Approach';
      default:
        return arch;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Space Planning Review</CardTitle>
          <CardDescription>
            Review the client's space planning wizard submission and AI recommendation
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Client's Answers */}
      <Card>
        <CardHeader>
          <CardTitle>Client's Answers</CardTitle>
          <CardDescription>Responses from the space planning wizard</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(session.answers).map(([key, value]) => (
              <div key={key} className="border rounded-lg p-3">
                <p className="text-sm font-medium text-gray-700 mb-1">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                </p>
                <p className="text-base text-gray-900">{getAnswerLabel(key, value as string)}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* AI Recommendation */}
      <Card>
        <CardHeader>
          <CardTitle>AI Recommendation</CardTitle>
          <CardDescription>AI-generated space architecture recommendation</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <Badge className="bg-blue-600">
                {getArchitectureLabel(session.aiRecommendation.architecture)}
              </Badge>
              <span className="text-sm text-blue-700">
                Confidence: {session.aiRecommendation.confidence}%
              </span>
            </div>
            <p className="text-gray-800">{session.aiRecommendation.reasoning}</p>
          </div>

          {/* Suggested Spaces */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Suggested Spaces:</h4>
            <div className="space-y-3">
              {session.aiRecommendation.suggestedSpaces.map((space, index) => (
                <div key={index} className="border border-l-4 border-l-blue-500 rounded-lg p-4 bg-white">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Briefcase className="size-5 text-blue-600" />
                        <h5 className="font-semibold text-gray-900">{space.name}</h5>
                        <Badge className="text-xs">{space.type.toUpperCase()}</Badge>
                      </div>
                      {space.teams.length > 0 && (
                        <p className="text-sm text-gray-600">Teams: {space.teams.join(', ')}</p>
                      )}
                      {space.estimatedTicketVolume && (
                        <p className="text-xs text-gray-500 mt-1">
                          Estimated volume: {space.estimatedTicketVolume}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Warnings */}
          {session.aiRecommendation.warnings && session.aiRecommendation.warnings.length > 0 && (
            <Card className="border-yellow-200 bg-yellow-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="size-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-900 mb-2">AI Considerations:</p>
                    <ul className="text-sm text-yellow-800 space-y-1">
                      {session.aiRecommendation.warnings.map((warning, index) => (
                        <li key={index}>• {warning}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* Decision Panel */}
      <Card>
        <CardHeader>
          <CardTitle>Your Decision</CardTitle>
          <CardDescription>Approve, modify, or reject the space plan</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Decision Options */}
          <div className="space-y-3">
            <div
              className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                decision === 'approve'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
              }`}
              onClick={() => setDecision('approve')}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    decision === 'approve' ? 'border-green-500 bg-green-500' : 'border-gray-300'
                  }`}
                >
                  {decision === 'approve' && <CheckCircle2 className="size-3 text-white" />}
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Approve AI Recommendation</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Accept the AI recommendation as-is and create spaces
                  </div>
                </div>
              </div>
            </div>

            <div
              className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                decision === 'modify'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
              }`}
              onClick={() => setDecision('modify')}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    decision === 'modify' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                  }`}
                >
                  {decision === 'modify' && <Edit className="size-3 text-white" />}
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Modify Recommendation</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Adjust the space plan based on technical factors
                  </div>
                </div>
              </div>
            </div>

            <div
              className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                decision === 'reject'
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-200 hover:border-red-300 hover:bg-gray-50'
              }`}
              onClick={() => setDecision('reject')}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    decision === 'reject' ? 'border-red-500 bg-red-500' : 'border-gray-300'
                  }`}
                >
                  {decision === 'reject' && <XCircle className="size-3 text-white" />}
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Reject and Send Back</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Send back to client with feedback for revision
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modification Interface */}
          {decision === 'modify' && (
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-gray-900">Modified Space Plan:</h4>
                <Button variant="outline" size="sm" onClick={addSpace}>
                  Add Space
                </Button>
              </div>
              <div className="space-y-3">
                {modifiedSpaces.map((space, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 space-y-2">
                        <input
                          type="text"
                          value={space.name}
                          onChange={(e) => updateSpace(index, 'name', e.target.value)}
                          className="w-full px-3 py-2 border rounded-md"
                          placeholder="Space name"
                        />
                        <input
                          type="text"
                          value={space.teams.join(', ')}
                          onChange={(e) => updateSpace(index, 'teams', e.target.value.split(',').map((t) => t.trim()))}
                          className="w-full px-3 py-2 border rounded-md"
                          placeholder="Teams (comma-separated)"
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSpace(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <XCircle className="size-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Notes to client {decision === 'reject' && '(required)'}:
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={
                decision === 'reject'
                  ? 'Explain why the plan needs revision...'
                  : 'Optional notes about your decision...'
              }
              rows={4}
              className="w-full"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            {decision === 'reject' ? (
              <Button
                onClick={handleReject}
                disabled={!notes.trim() || submitting}
                variant="destructive"
                className="gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <XCircle className="size-4" />
                    Send Back to Client
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleApprove}
                disabled={!decision || submitting}
                className="gap-2 bg-green-600 hover:bg-green-700"
              >
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Creating Spaces...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="size-4" />
                    {decision === 'modify' ? 'Approve Modified Plan' : 'Approve & Create Spaces'}
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
